import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MagnifyingGlass, MapPin, Briefcase, Clock, GraduationCap, CurrencyInr, X } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import SectionHeader from "@/components/site/SectionHeader";
import { INDUSTRIES } from "@/components/site/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (!detail) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

export default function JobsPage() {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [industry, setIndustry] = useState("");
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/jobs`);
        setJobs(data);
      } catch {
        toast.error("Failed to load jobs");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return jobs.filter((j) => {
      const matchQ = q ? (j.title + j.description + j.industry).toLowerCase().includes(q.toLowerCase()) : true;
      const matchLoc = location ? j.location.toLowerCase().includes(location.toLowerCase()) : true;
      const matchInd = industry ? j.industry === industry : true;
      return matchQ && matchLoc && matchInd;
    });
  }, [jobs, q, location, industry]);

  return (
    <div data-testid="jobs-page">
      <section className="section-pad bg-white">
        <div className="container-tight">
          <SectionHeader overline="Careers at CRETRE clients" title="Looking for Your Next Opportunity?" subtitle="Explore current opportunities across Ahmedabad and other locations." />

          <div className="mt-10 grid gap-3 md:grid-cols-[1.4fr,1fr,1fr,auto] bg-secondary/50 border border-border rounded-2xl p-4">
            <div className="relative">
              <MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="jobs-search-input" placeholder="Job title, keyword..." value={q} onChange={(e) => setQ(e.target.value)} className="pl-9 h-11 bg-white" />
            </div>
            <Input data-testid="jobs-location-input" placeholder="Location" value={location} onChange={(e) => setLocation(e.target.value)} className="h-11 bg-white" />
            <select
              data-testid="jobs-industry-select"
              value={industry}
              onChange={(e) => setIndustry(e.target.value)}
              className="h-11 bg-white border border-input rounded-md px-3 text-sm text-navy-800"
            >
              <option value="">All Industries</option>
              {INDUSTRIES.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
            </select>
            <Button
              data-testid="jobs-clear-filters"
              variant="outline"
              onClick={() => { setQ(""); setLocation(""); setIndustry(""); }}
              className="h-11 rounded-md"
            >
              <X size={16} className="mr-1" /> Clear
            </Button>
          </div>

          <div className="mt-8 text-sm text-muted-foreground" data-testid="jobs-result-count">{loading ? "Loading jobs..." : `${filtered.length} open position${filtered.length === 1 ? "" : "s"}`}</div>

          <div className="mt-4 grid gap-4">
            {filtered.map((j) => (
              <div key={j.id} data-testid={`job-card-${j.id}`} className="bg-white rounded-2xl border border-border p-6 lg:p-8 hover:border-gold-400/60 hover:shadow-lg transition-all">
                <div className="grid gap-6 lg:grid-cols-[1fr,auto] items-start">
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="overline">{j.industry}</span>
                    </div>
                    <h3 className="mt-2 font-serif text-2xl text-navy-800">{j.title}</h3>
                    <p className="mt-2 text-sm text-muted-foreground leading-relaxed line-clamp-2">{j.description}</p>
                    <div className="mt-5 grid grid-cols-2 md:grid-cols-3 gap-3 text-sm text-navy-800">
                      <Meta icon={MapPin} label={j.location} />
                      <Meta icon={CurrencyInr} label={j.salary} />
                      <Meta icon={Briefcase} label={j.experience} />
                      <Meta icon={GraduationCap} label={j.qualification} />
                      <Meta icon={Clock} label={`${j.employment_type} · ${j.shift}`} />
                    </div>
                  </div>
                  <div className="lg:pl-6 lg:border-l lg:border-border flex lg:flex-col gap-3">
                    <Button
                      data-testid={`job-apply-btn-${j.id}`}
                      onClick={() => setApplying(j)}
                      className="bg-navy-800 hover:bg-navy-700 text-white rounded-full px-6 h-11 btn-lift"
                    >
                      Apply Now
                    </Button>
                  </div>
                </div>
              </div>
            ))}
            {!loading && filtered.length === 0 && (
              <div data-testid="jobs-empty" className="bg-secondary/40 rounded-2xl border border-border p-10 text-center text-muted-foreground">
                No matching openings right now. Try clearing filters or check back soon.
              </div>
            )}
          </div>
        </div>
      </section>

      <ApplyDialog job={applying} onClose={() => setApplying(null)} />
    </div>
  );
}

function Meta({ icon: Icon, label }) {
  return (
    <div className="flex items-center gap-2 text-muted-foreground"><Icon size={16} className="text-gold-500 shrink-0" /><span>{label}</span></div>
  );
}

function ApplyDialog({ job, onClose }) {
  const [form, setForm] = useState({
    full_name: "", mobile: "", email: "", current_location: "", qualification: "",
    experience: "", current_company: "", preferred_location: "", position_applied: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (job) setForm((f) => ({ ...f, position_applied: job.title, preferred_location: job.location }));
  }, [job]);

  const on = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/job-applications`, { ...form, job_id: job?.id });
      toast.success("Application submitted", { description: "Our recruitment team will contact you shortly." });
      onClose();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={!!job} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="apply-dialog" className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-navy-800">Apply for {job?.title}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
          <Field label="Full Name*" testid="apply-full-name"><Input required value={form.full_name} onChange={on("full_name")} /></Field>
          <Field label="Mobile Number*" testid="apply-mobile"><Input required value={form.mobile} onChange={on("mobile")} /></Field>
          <Field label="Email*" testid="apply-email"><Input required type="email" value={form.email} onChange={on("email")} /></Field>
          <Field label="Current Location*" testid="apply-current-location"><Input required value={form.current_location} onChange={on("current_location")} /></Field>
          <Field label="Qualification*" testid="apply-qualification"><Input required value={form.qualification} onChange={on("qualification")} /></Field>
          <Field label="Total Experience*" testid="apply-experience"><Input required value={form.experience} onChange={on("experience")} placeholder="e.g. 2 years" /></Field>
          <Field label="Current / Last Company" testid="apply-company"><Input value={form.current_company} onChange={on("current_company")} /></Field>
          <Field label="Preferred Location" testid="apply-preferred-location"><Input value={form.preferred_location} onChange={on("preferred_location")} /></Field>
          <div className="sm:col-span-2">
            <Field label="Position Applied For*" testid="apply-position"><Input required value={form.position_applied} onChange={on("position_applied")} /></Field>
          </div>
          <DialogFooter className="sm:col-span-2 mt-2">
            <Button data-testid="apply-submit-btn" type="submit" disabled={submitting} className="bg-navy-800 hover:bg-navy-700 text-white rounded-full h-11 px-8">
              {submitting ? "Submitting..." : "Submit Application"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, testid, children }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wide text-navy-800 font-semibold">{label}</Label>
      <div data-testid={testid} className="mt-1.5">{children}</div>
    </div>
  );
}
